import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./revenueForecastCalc.css";
import { hasAnyPermission } from "../../permissions";

// PHASE 2 21 MAY 2026 STARTS HERE
const ROLES = {
  SUPER_ADMIN: "SUPER ADMIN",
  ADMIN: "ADMIN",
  OTA_MANAGER: "OTA MANAGER",
  OTA_EXECUTIVE: "OTA EXECUTIVE",
  SALES_EXECUTIVE: "SALES EXECUTIVE",
  ONBOARDING_MANAGER: "ONBOARDING MANAGER",
  ONBOARDING_EXECUTIVE: "ONBOARDING EXECUTIVE",
};
// ENDS HERE

function RevenueForecastCalc() {
  const navigate = useNavigate();

  const loggedInRole = localStorage.getItem("loggedInRole");

  const allowedRevenueCalculatorRoles = [
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.OTA_MANAGER,
  ];

  // Granular permission #6 (READ PROPERTY-WISE TARGETS & WRITE REVENUE
  // CALCULATOR) additionally grants access here, on top of the existing
  // role-based check — never a restriction, only an OR.
  const canAccessRevenueCalculator =
    allowedRevenueCalculatorRoles.includes(loggedInRole) ||
    hasAnyPermission(["read_targets_write_revenue_calc"]);

  useEffect(() => {
    if (!localStorage.getItem("isAuthenticated")) {
      navigate("/admin");
      return;
    }

    if (!canAccessRevenueCalculator) {
      alert("Access denied. You cannot access Revenue Calculator.");
      navigate("/HomePageRrc");
    }
  }, [loggedInRole, navigate, canAccessRevenueCalculator]);

  const [propertyName, setPropertyName] = useState("");
  const [annualTarget, setAnnualTarget] = useState("");
  const [occupancyPercentage, setOccupancyPercentage] = useState("");
  const [numRooms, setNumRooms] = useState("");
  const [numCategories, setNumCategories] = useState("");

  const [lowSeasons, setLowSeasons] = useState([]);
  const [midSeasons, setMidSeasons] = useState([]);
  const [highSeasons, setHighSeasons] = useState([]);

  const [submitted, setSubmitted] = useState(false);
  const [expectedDailyTarget, setExpectedDailyTarget] = useState(0); // expected daily target revenue

  // categories will be auto-sized based on numCategories
  const [categories, setCategories] = useState([]);

  // RRC PHASE 2
const [seasonRatePercents, setSeasonRatePercents] = useState({
  lowWeekday: "30",
  lowWeekend: "20",
  midWeekday: "10",
  peakWeekday: "20",
  peakWeekend: "30",
});

const updateSeasonRatePercent = (key, value) => {
  setSeasonRatePercents((prev) => ({
    ...prev,
    [key]: value,
  }));
};

const getPercentNumber = (value, fallback = 0) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : fallback;
};
  
// CITY PRESETS
const CITY_PRESETS = {
  Rishikesh: {
    low: ["January", "July", "August", "September"],
    mid: ["February", "October", "November", "December"],
    high: ["March", "April", "May", "June"],
    occupancy: {
      January: 40,
      February: 50,
      March: 75,
      April: 80,
      May: 85,
      June: 90,
      July: 35,
      August: 40,
      September: 45,
      October: 60,
      November: 60,
      December: 65,
    },
  },

  Lucknow: {
    low: ["May", "June", "July", "August"],
    mid: ["March", "April", "September", "October"],
    high: ["November", "December", "January", "February"],
    occupancy: {
      May: 50,
      June: 50,
      July: 50,
      August: 50,
      March: 65,
      April: 65,
      September: 55,
      October: 60,
      November: 70,
      December: 70,
      January: 70,
      February: 70,
    },
  },

  Varanasi: {
    low: ["April", "May", "June", "July"],
    mid: ["August", "September", "January", "March"],
    high: ["October", "November", "December", "February"],
    occupancy: {
      April: 40,
      May: 40,
      June: 35,
      July: 40,
      August: 50,
      September: 50,
      January: 70,
      March: 70,
      October: 70,
      November: 80,
      December: 85,
      February: 90,
    },
  },

  Corbett: {
    low: ["January", "July", "August", "September"],
    mid: ["February", "October", "November", "December"],
    high: ["March", "April", "May", "June"],
    occupancy: {
      January: 30,
      July: 30,
      August: 30,
      September: 30,
      February: 50,
      October: 50,
      November: 50,
      December: 50,
      March: 70,
      April: 70,
      May: 70,
      June: 70,
    },
  },

  Ayodhya: {
    low: ["May", "June", "July", "August"],
    mid: ["March", "April", "January", "September"],
    high: ["February", "October", "November", "December"],
    occupancy: {
      May: 45,
      June: 35,
      July: 35,
      August: 35,
      March: 70,
      April: 55,
      January: 70,
      September: 55,
      February: 80,
      October: 75,
      November: 80,
      December: 80,
    },
  },

  Dehradun: {
    low: ["January", "July", "August", "September"],
    mid: ["February", "October", "November", "December"],
    high: ["March", "April", "May", "June"],
    occupancy: {
      January: 30,
      July: 30,
      August: 30,
      September: 30,
      February: 50,
      October: 50,
      November: 50,
      December: 50,
      March: 70,
      April: 70,
      May: 70,
      June: 70,
    },
  },

  Mussoorie: {
    low: ["January", "July", "August", "September"],
    mid: ["February", "October", "November", "December"],
    high: ["March", "April", "May", "June"],
    occupancy: {
      January: 40,
      July: 35,
      August: 40,
      September: 40,
      February: 55,
      October: 65,
      November: 65,
      December: 70,
      March: 70,
      April: 75,
      May: 80,
      June: 80,
    },
  },

  Lansdowne: {
    low: ["January", "July", "August", "September"],
    mid: ["February", "October", "November", "December"],
    high: ["March", "April", "May", "June"],
    occupancy: {
      January: 30,
      July: 30,
      August: 30,
      September: 30,
      February: 50,
      October: 50,
      November: 50,
      December: 50,
      March: 70,
      April: 70,
      May: 70,
      June: 70,
    },
  },
};

const [selectedCityPreset, setSelectedCityPreset] = useState("");

const applyCityPreset = (cityName) => {
  const preset = CITY_PRESETS[cityName];
  if (!preset) return;

  setLowSeasons(preset.low);
  setMidSeasons(preset.mid);
  setHighSeasons(preset.high);
  setMonthOccupancies(preset.occupancy);
};

  // VARS FOR DIFFERENT WEEKLY VAR STORING STARTS HERE
  const getDefaultWeekInputs = () => ({
  week1: "1",
  week2: "1",
  week3: "1",
  week4: "1",
});

const [weekInputsByMonth, setWeekInputsByMonth] = useState({});
  
const getMonthWeekInputs = (year, monthIdx) => {
  const key = ymKey(year, monthIdx);
  return weekInputsByMonth[key] || getDefaultWeekInputs();
};

const updateWeekInput = (year, monthIdx, key, value) => {
  const monthKey = ymKey(year, monthIdx);

  setWeekInputsByMonth((prev) => ({
    ...prev,
    [monthKey]: {
      ...getDefaultWeekInputs(),
      ...(prev[monthKey] || {}),
      [key]: value,
    },
  }));
};
  // VARS ENDS HERE
  
  // per-month season occupancy selection (e.g. { January: 35, February: 55, ... })
  const [monthOccupancies, setMonthOccupancies] = useState({});

  // "Assign To" selection
  const [assignedTo, setAssignedTo] = useState("");

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // LIVE CALENDER VARS ADDED ON 26 FEB 2026
  // ---- Calendar (Live) ----
const today = new Date();
const [calYear, setCalYear] = useState(today.getFullYear());
const [calMonth, setCalMonth] = useState(today.getMonth()); // 0 = Jan
const [selectedDate, setSelectedDate] = useState(null);

const monthNames = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const getDaysInMonth = (year, monthIdx) => new Date(year, monthIdx + 1, 0).getDate();
const getFirstDayOfMonth = (year, monthIdx) => new Date(year, monthIdx, 1).getDay(); // 0=Sun

const goPrevMonth = () => {
  setCalMonth((prev) => {
    if (prev === 0) {
      setCalYear((y) => y - 1);
      return 11;
    }
    return prev - 1;
  });
};

const goNextMonth = () => {
  setCalMonth((prev) => {
    if (prev === 11) {
      setCalYear((y) => y + 1);
      return 0;
    }
    return prev + 1;
  });
};

const isSameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();



const toISODate = (d) => {
  // local ISO yyyy-mm-dd (no timezone shifting)
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const ymKey = (year, monthIdx) => `${year}-${String(monthIdx + 1).padStart(2, "0")}`;

// ✅ Weighted daily split using WEEK_VAR (sum exactly equals monthTarget)
const WEEK_VAR = {
  0: 0.9,   // Sun
  1: 0.7,   // Mon
  2: 0.7,   // Tue
  3: 0.77,  // Wed
  4: 0.9,   // Thu
  5: 1.35,  // Fri
  6: 1.68,  // Sat
};
  // WEEKLY LOGIC DIVISION 3 MARCH
const toNum = (v, fallback = 1) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

// weekIndex: 0=week1 (days 1-7), 1=week2 (8-14), 2=week3 (15-21), 3=week4 (22-end)
const getWeekIndexByDay = (day) => Math.min(3, Math.floor((day - 1) / 7));

  // CALENDAR PER DAY INPUT VARIABLE STARTS HERE
  // ✅ Per-day multipliers (decimal allowed)
const [dayInputs, setDayInputs] = useState({}); // { "YYYY-MM-DD": "1.25", ... }

const updateDayInput = (iso, value) => {
  // allow empty (means "not entered" -> no multiplication)
  if (value === "") {
    setDayInputs((prev) => {
      const copy = { ...prev };
      delete copy[iso];
      return copy;
    });
    return;
  }

  setDayInputs((prev) => ({ ...prev, [iso]: value }));
};

const toFloatOr0 = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const getWeekRequiredSum = (year, monthIdx, weekIndex) => {
  // weekIndex: 0=days 1-7, 1=8-14, 2=15-21, 3=22-end
  if (weekIndex < 3) return 7;
  const days = getDaysInMonth(year, monthIdx);
  return Math.max(0, days - 21); // March: 10, April: 9, Feb(28): 7
};

// ✅ compute week sums for CURRENT month in calendar
const weekSums = useMemo(() => {
  const sums = [0, 0, 0, 0];
  const days = getDaysInMonth(calYear, calMonth);

  for (let day = 1; day <= days; day++) {
    const iso = toISODate(new Date(calYear, calMonth, day));
    const wi = getWeekIndexByDay(day);
    sums[wi] += toFloatOr0(dayInputs[iso]);
  }
  return sums;
}, [calYear, calMonth, dayInputs]);

const weekDiffs = useMemo(() => {
  return [0, 1, 2, 3].map((wi) => {
    const req = getWeekRequiredSum(calYear, calMonth, wi);
    const sum = weekSums[wi] || 0;
    return { wi, req, sum, diff: +(req - sum).toFixed(2) };
  });
}, [calYear, calMonth, weekSums]);
  // CALENDAR PER DAY VARIKABLES ENDS HERE
  
/**
 * ✅ MonthTarget -> WeeklyTarget (week multipliers)
 * ✅ WeeklyTarget -> DailyTarget (weekday weights, normalized within that week)
 * Week 4 automatically becomes "remaining days" (28/29/30/31 => 7/8/9/10 days)
 */
const buildDailyTargetsForMonthWithWeeks = (year, monthIdx, monthTarget, weekInputs) => {
  const daysInThisMonth = getDaysInMonth(year, monthIdx);
  const roundedMonthTarget = Math.round(monthTarget || 0);
  if (!daysInThisMonth || roundedMonthTarget <= 0) return {};

  // 1) multipliers (default = 1)
  const multipliers = [
    toNum(weekInputs?.week1, 1),
    toNum(weekInputs?.week2, 1),
    toNum(weekInputs?.week3, 1),
    toNum(weekInputs?.week4, 1),
  ];

  const sumMul = multipliers.reduce((a, b) => a + b, 0) || 4;

   
  
  // 2) compute weekly targets based on multipliers
  // baseWeekly = monthTarget / sum(multipliers)
  const baseWeekly = roundedMonthTarget / sumMul;
  const weeklyTargets = multipliers.map((m) => baseWeekly * m);

  // 3) Prepare per-week day lists + weekWeightSums
  const weeks = [[], [], [], []]; // store items { iso, w }
  const weekWeightSum = [0, 0, 0, 0];

  for (let day = 1; day <= daysInThisMonth; day++) {
    const d = new Date(year, monthIdx, day);
    const iso = toISODate(d);
    const w = WEEK_VAR[d.getDay()] ?? 1;

    const wi = getWeekIndexByDay(day);
    weeks[wi].push({ iso, w });
    weekWeightSum[wi] += w;
  }

  // 4) Allocate each weekTarget into that week's days (normalize by weekWeightSum)
  //    Use rounding + last-day remainder to ensure each week sums EXACT.
  const result = {};
  let monthRunningSum = 0;

  for (let wi = 0; wi < 4; wi++) {
    const items = weeks[wi];
    if (!items.length) continue;

    const weekTargetRounded = Math.round(weeklyTargets[wi]);
    const totalW = weekWeightSum[wi] || 1;

    let weekRunning = 0;

    for (let i = 0; i < items.length; i++) {
      const { iso, w } = items[i];

      if (i < items.length - 1) {
        const val = Math.round((weekTargetRounded * w) / totalW);
        result[iso] = val;
        weekRunning += val;
      } else {
        // last day in this week = remainder (so week total matches)
        const remainder = weekTargetRounded - weekRunning;
        result[iso] = remainder;
        weekRunning += remainder;
      }
    }

    monthRunningSum += weekRunning;
  }

  // 5) Final safety: ensure month sum == roundedMonthTarget (fix last date of month)
  const lastISO = toISODate(new Date(year, monthIdx, daysInThisMonth));
  const diff = roundedMonthTarget - monthRunningSum;
  if (diff !== 0 && result[lastISO] != null) {
    result[lastISO] = (result[lastISO] || 0) + diff;
  }

  return result;
};
  // LOGIC ENDS HERE

  // LIVE CALENDAR ENDS HERE
  

// INTEGRATION PHASE START 22 JAN


// INTEGRATION PHASE END

  // ✅ Weekly variables (sum = 7). getDay(): 0=Sun,1=Mon,...6=Sat




  // Assign To options - usernames of every user in Settings whose role
  // is OTA EXECUTIVE or SALES EXECUTIVE, fetched fresh rather than
  // hardcoded.
  const [assignOptions, setAssignOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchAgents = async () => {
      try {
        const res = await fetch("/api/users");
        const data = await res.json();
        if (cancelled) return;

        const otaExecutives = (Array.isArray(data) ? data : [])
          .filter((u) => u && u.username && ["OTA EXECUTIVE", "SALES EXECUTIVE"].includes(u.role))
          .map((u) => u.username);

        setAssignOptions(otaExecutives);
      } catch (err) {
        console.error("Failed to load agent list:", err);
      }
    };

    fetchAgents();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleMultiSelectChange = (e, setter) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setter(selected);
  };

  // When Number of Categories changes, resize categories array to that length
 // When Number of Categories changes, resize categories array to that length
const handleNumCategoriesChange = (e) => {
  const value = e.target.value;
  setNumCategories(value);

  const count = parseInt(value, 10) || 0;

  setCategories((prev) => {
    const next = [];

    for (let i = 0; i < count; i++) {
      const existing = prev[i] || {
        name: "",
        rooms: "",
        percentage: "",
        isPercentageEdited: false,
      };

      next.push({
        ...existing,
        name: existing.name?.trim() ? existing.name : `c${i + 1}`,
      });
    }

    return next;
  });
};


 const updateCategory = (index, field, value) => {
  setCategories((prev) => {
    const copy = [...prev];
    const existing = copy[index] || {};

    copy[index] = {
      ...existing,
      [field]: value,
      ...(field === "percentage" ? { isPercentageEdited: true } : {}),
    };

    return copy;
  });
};

  // HELPER FUNCTION STARTS HERE
// ✅ Prefill per-day inputs with "1" for the full calendar year
const buildDefaultDayInputsForYear = (year) => {
  const out = {};
  for (let m = 0; m < 12; m++) {
    const days = getDaysInMonth(year, m);
    for (let d = 1; d <= days; d++) {
      const iso = toISODate(new Date(year, m, d));
      out[iso] = "1";
    }
  }
  return out;
};
  // HELPER FUNCTION ENDS HERE

  // HELPER FUNCTION STARTS HERE
const getCategoryWeight = (index) => 1.5 + index * 0.5;

const computedCategoryData = useMemo(() => {
  const rows = categories.map((cat, index) => {
    const weight = getCategoryWeight(index);
    const rooms = parseFloat(cat.rooms) || 0;
    const calculatedWeight = +(rooms * weight).toFixed(2);

    return {
      ...cat,
      weight,
      calculatedWeight,
    };
  });

  const caltotal = rows.reduce(
    (sum, row) => sum + (row.calculatedWeight || 0),
    0
  );

  return {
    rows: rows.map((row) => {
      const autoRevenuePercentage =
        caltotal > 0
          ? +(((row.calculatedWeight || 0) / caltotal) * 100).toFixed(2)
          : 0;

      return {
        ...row,
        autoRevenuePercentage,
      };
    }),
    caltotal: +caltotal.toFixed(2),
  };
}, [categories]);
  useEffect(() => {
  setCategories((prev) => {
    let changed = false;

    const next = prev.map((cat, index) => {
      if (cat.isPercentageEdited) return cat;

      const computed = computedCategoryData.rows[index];
      const nextPercentage =
        computed && computed.autoRevenuePercentage > 0
          ? String(computed.autoRevenuePercentage)
          : "";

      if ((cat.percentage || "") !== nextPercentage) {
        changed = true;
        return {
          ...cat,
          percentage: nextPercentage,
        };
      }

      return cat;
    });

    return changed ? next : prev;
  });
}, [computedCategoryData]);
  // HELPER FUNCTION ENDS HERE
  const handleSubmit = (e) => {
  e.preventDefault();

  setSubmitted(true);

  const buildDefaultWeekInputsForYear = (year) => {
  const out = {};
  for (let m = 0; m < 12; m++) {
    out[ymKey(year, m)] = {
      week1: "1",
      week2: "1",
      week3: "1",
      week4: "1",
    };
  }
  return out;
};

  const annualRevenueNum = parseFloat(annualTarget) || 0;
  const occNum = parseFloat(occupancyPercentage) || 0;

  let totalDesired = 0;
  if (annualRevenueNum > 0 && occNum > 0) {
    totalDesired = annualRevenueNum / (occNum / 100);
  }

  const dailyTarget = totalDesired > 0 ? totalDesired / 12 / 30 : 0;
  setExpectedDailyTarget(dailyTarget);
setWeekInputsByMonth(buildDefaultWeekInputsForYear(calYear));
  // ✅ Reset ALL day multipliers to 1 (on every GET TARGETS click)
  setDayInputs(buildDefaultDayInputsForYear(calYear));
    

  // ✅ optional: clear previously selected date
  setSelectedDate(null);
};

  const isMonthInOtherSeason = (month, currentSeason) => {
    if (currentSeason !== "low" && lowSeasons.includes(month)) return true;
    if (currentSeason !== "mid" && midSeasons.includes(month)) return true;
    if (currentSeason !== "high" && highSeasons.includes(month)) return true;
    return false;
  };

  const joinedLow = submitted && lowSeasons.length ? lowSeasons.join(", ") : "-";
  const joinedMid = submitted && midSeasons.length ? midSeasons.join(", ") : "-";
  const joinedHigh =
    submitted && highSeasons.length ? highSeasons.join(", ") : "-";

  // helper: which season a month belongs to
  const getSeasonForMonth = (m) => {
    if (highSeasons.includes(m)) return "high";
    if (midSeasons.includes(m)) return "mid";
    if (lowSeasons.includes(m)) return "low";
    return null;
  };

  // handle occupancy radio change per month
  const handleMonthOccupancyChange = (month, value) => {
    setMonthOccupancies((prev) => ({
      ...prev,
      [month]: value,
    }));
  };

  // get possible occupancy options for a month based on its season
  const getOccupancyOptionsForMonth = (month) => {
    const season = getSeasonForMonth(month);
    if (season === "low") return [30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90];
    if (season === "mid") return [30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90];
    if (season === "high") return [30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90];
    return [];
  };

  // ---------- Annual Desired Revenue calculations (for TOTAL row) ----------
  const annualRows = months.map((m) => {
    const seasonKey = getSeasonForMonth(m);

   const daysInMonth = new Date(
  calYear,
  months.indexOf(m) + 1,
  0
).getDate();
    
    // Daily revenue from weekday values
    let dailyRevenue = 0;

    if (submitted && seasonKey) {
  computedCategoryData.rows.forEach((cat) => { 
    const catPerc = parseFloat(cat.percentage) || 0;
        const catRooms = parseFloat(cat.rooms) || 0;
        if (!catRooms || !catPerc) return;

        const revIntoCategory = expectedDailyTarget * (catPerc / 100);
        const perDayPerRoom =
          catRooms > 0 ? revIntoCategory / catRooms : 0;

        let weekdayVal = 0;
       const lowWeekdayPercent = getPercentNumber(seasonRatePercents.lowWeekday, 30);
const midWeekdayPercent = getPercentNumber(seasonRatePercents.midWeekday, 10);
const peakWeekdayPercent = getPercentNumber(seasonRatePercents.peakWeekday, 20);

if (seasonKey === "low") {
  weekdayVal = perDayPerRoom * (1 - lowWeekdayPercent / 100);
} else if (seasonKey === "mid") {
  weekdayVal = perDayPerRoom * (1 - midWeekdayPercent / 100);
} else if (seasonKey === "high") {
  weekdayVal = perDayPerRoom * (1 + peakWeekdayPercent / 100);
}

        dailyRevenue += weekdayVal * catRooms;
      });
    }

// ✅ Build daily targets for ALL months (YYYY-MM -> { yyyy-mm-dd: value })

    
    // Occupancy from user's selection in SEASON OCCUPANCY %
    const occSelected = monthOccupancies[m] || 0; // e.g. 35
    const occFraction =
      submitted && occSelected > 0 ? occSelected / 100 : 0;

    // NEWADR LOGIC:
    const roomsNum = parseFloat(numRooms) || 0;

   const adr =
   submitted && dailyRevenue > 0 && roomsNum > 0
    ? Math.round(dailyRevenue / roomsNum)
    : 0;

    // TARGET REVENUE (still calculated but NOT displayed)
    const targetRevenue =
      submitted && occFraction > 0 && dailyRevenue > 0
        ? dailyRevenue * occFraction * daysInMonth
        : 0;

    return {
      month: m,
      daysInMonth,
      dailyRevenue,
      occSelected,
      adr,
      targetRevenue,
    };
  });

const dailyTargetsByMonth = useMemo(() => {
  if (!submitted) return {};

  const out = {};
  for (let mi = 0; mi < 12; mi++) {
    const mName = monthNames[mi];
    const row = annualRows.find((r) => r.month === mName);
    const monthTarget = row?.targetRevenue || 0;
    const monthKey = ymKey(calYear, mi);
    const monthWeekInputs =
      weekInputsByMonth[monthKey] || getDefaultWeekInputs();

    out[monthKey] = buildDailyTargetsForMonthWithWeeks(
      calYear,
      mi,
      monthTarget,
      monthWeekInputs
    );
  }
  return out;
}, [submitted, annualRows, calYear, weekInputsByMonth]);

  const dayInputsNumber = useMemo(() => {
  return Object.fromEntries(
    Object.entries(dayInputs || {}).map(([k, v]) => [k, parseFloat(v)])
  );
}, [dayInputs]);

   function applyDayMultipliersToDailyTargets(dailyTargetsByMonth, dayInputs) {
  const out = {};

  for (const [ym, dayMap] of Object.entries(dailyTargetsByMonth || {})) {
    const nextMap = {};
    for (const [iso, baseVal] of Object.entries(dayMap || {})) {
      const raw = dayInputs?.[iso];
      const hasInput = raw !== undefined && raw !== "";
      const mult = hasInput ? parseFloat(raw) : 1;
      const safeMult = Number.isFinite(mult) ? mult : 1;
      nextMap[iso] = Math.round((baseVal || 0) * safeMult);
    }
    out[ym] = nextMap;
  }

  return out;
}
  
const finalDailyTargetsByMonth = useMemo(() => {
  if (!submitted) return {};
  return applyDayMultipliersToDailyTargets(dailyTargetsByMonth, dayInputsNumber);
}, [submitted, dailyTargetsByMonth, dayInputsNumber]);
 
  
  let totalOcc = 0;
  let occCount = 0;
  let totalRevenue = 0;
  let adrTotal = 0;
  let adrCount = 0;

  annualRows.forEach((row) => {
    if (row.occSelected > 0) {
      totalOcc += row.occSelected;
      occCount++;
    }
    if (row.targetRevenue > 0) {
  totalRevenue += row.targetRevenue;
}

    if (row.adr > 0) {
      adrTotal += row.adr;
      adrCount++;
    }
  });

  const avgOcc = occCount > 0 ? totalOcc / occCount : 0;
  // ✅ ROOM VALIDATION (Total Rooms vs Category Rooms Sum)
const totalRoomsNum = parseInt(numRooms, 10) || 0;

const categoryRoomsSum = categories.reduce((sum, cat) => {
  return sum + (parseInt(cat.rooms, 10) || 0);
}, 0);

const showRoomWarning =
  totalRoomsNum > 0 &&
  categories.length > 0 &&
  categoryRoomsSum !== totalRoomsNum;

  const avgAdr = adrCount > 0 ? adrTotal / adrCount : 0;

const weeklyTargetsByMonth = useMemo(() => {
  if (!submitted) return {};

  const out = {};

  for (let mi = 0; mi < 12; mi++) {
    const monthKey = ymKey(calYear, mi);
    const mName = monthNames[mi];
    const row = annualRows.find((r) => r.month === mName);
    const monthTarget = Math.round(row?.targetRevenue || 0);

    const weekInputs = weekInputsByMonth[monthKey] || getDefaultWeekInputs();

    const multipliers = [
      toNum(weekInputs?.week1, 1),
      toNum(weekInputs?.week2, 1),
      toNum(weekInputs?.week3, 1),
      toNum(weekInputs?.week4, 1),
    ];

    const sumMul = multipliers.reduce((a, b) => a + b, 0) || 4;
    const baseWeekly = monthTarget / sumMul;

    const rawWeeklyTargets = multipliers.map((m) => baseWeekly * m);

    const roundedWeeklyTargets = rawWeeklyTargets.map((v) => Math.round(v));

    const weeklyTotal = roundedWeeklyTargets.reduce((a, b) => a + b, 0);
    const diff = monthTarget - weeklyTotal;

    // adjust last week so total matches exact month target
    roundedWeeklyTargets[3] = (roundedWeeklyTargets[3] || 0) + diff;

    out[monthKey] = {
      month: mName,
      monthTarget,
      weekInputs: {
        week1: toNum(weekInputs?.week1, 1),
        week2: toNum(weekInputs?.week2, 1),
        week3: toNum(weekInputs?.week3, 1),
        week4: toNum(weekInputs?.week4, 1),
      },
      weeklyTargets: {
        week1: roundedWeeklyTargets[0] || 0,
        week2: roundedWeeklyTargets[1] || 0,
        week3: roundedWeeklyTargets[2] || 0,
        week4: roundedWeeklyTargets[3] || 0,
      },
    };
  }

  return out;
}, [submitted, calYear, annualRows, weekInputsByMonth]);
  
    const buildPayload = () => {
  const categoriesWithRates = computedCategoryData.rows.map((cat) => {
    const catPerc = parseFloat(cat.percentage) || 0;
    const catRooms = parseFloat(cat.rooms) || 0;
    const revIntoCategory =
      submitted && catPerc > 0 ? expectedDailyTarget * (catPerc / 100) : 0;

    const perDayPerRoom =
      submitted && catRooms > 0 ? revIntoCategory / catRooms : 0;

   const lowWeekdayPercent = getPercentNumber(seasonRatePercents.lowWeekday, 30);
const lowWeekendPercent = getPercentNumber(seasonRatePercents.lowWeekend, 20);
const midWeekdayPercent = getPercentNumber(seasonRatePercents.midWeekday, 10);
const peakWeekdayPercent = getPercentNumber(seasonRatePercents.peakWeekday, 20);
const peakWeekendPercent = getPercentNumber(seasonRatePercents.peakWeekend, 30);

const rates = submitted
  ? {
      low: {
        weekday: perDayPerRoom * (1 - lowWeekdayPercent / 100),
        weekend: perDayPerRoom * (1 - lowWeekendPercent / 100),
      },
      mid: {
        weekday: perDayPerRoom * (1 - midWeekdayPercent / 100),
        weekend: perDayPerRoom,
      },
      high: {
        weekday: perDayPerRoom * (1 + peakWeekdayPercent / 100),
        weekend: perDayPerRoom * (1 + peakWeekendPercent / 100),
      },
    }
  : null;

    return {
      name: cat.name,
      rooms: cat.rooms,
      percentage: cat.percentage,
      weight: cat.weight,
      calculatedWeight: cat.calculatedWeight,
      rates,
    };
  });

 return {
  propertyName,
  annualTarget,
  occupancyPercentage,
  numRooms,
  numCategories,

  seasonRatePercents,

  seasons: { lowSeasons, midSeasons, highSeasons },
    monthOccupancies,

    assignedTo,
    weekInputsByMonth,
    weeklyTargetsByMonth,
    dayInputs: dayInputsNumber,
    dailyTargetsByMonth,
    finalDailyTargetsByMonth,

    computed: {
      submitted,
      expectedDailyTarget,
      annualRows,
      totalRevenue,
      avgOcc,
      avgAdr,
      categoriesWithRates,
      caltotal: computedCategoryData.caltotal,
      calYear,
    },
  };
};
const handleAssignSubmit = async (e) => {
  e.preventDefault();

  if (!assignedTo) {
    alert("Please select one option to assign.");
    return;
  }

  if (!submitted) {
    alert("Firstly use the Revenue calculator and then click GET TARGETS first.");
    return;
  }

  try {
    const payload = buildPayload();

    const res = await fetch("/api/revenue-forecast", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (!res.ok) {
      alert(data?.message || "Failed to save");
      return;
    }

    alert(`Targets saved successfully with record ID: ${data._id}`);
  } catch (err) {
    console.error(err);
    alert("Error saving to DB.");
  }
};

// ENDS HERE

  // ALL SAVE ENDS HERE
  
// Month target revenue from your existing annualRows (monthNames aligns with calMonth)
const currentMonthName = monthNames[calMonth];
const currentMonthRow = annualRows.find((r) => r.month === currentMonthName);
const monthTarget = submitted ? (currentMonthRow?.targetRevenue || 0) : 0;

   const currentWeekInputs = useMemo(() => {
  return getMonthWeekInputs(calYear, calMonth);
}, [calYear, calMonth, weekInputsByMonth]);
  
// Build daily targets for the calendar month
// ✅ Daily targets = monthTarget / numberOfDays (equal for all dates)
const dailyTargets = useMemo(() => {
  if (!submitted || !monthTarget || monthTarget <= 0) return {};

 return buildDailyTargetsForMonthWithWeeks(
  calYear,
  calMonth,
  monthTarget,
  currentWeekInputs
);
}, [submitted, calYear, calMonth, monthTarget, currentWeekInputs]);// ✅ add weekInputs
// ✅ REVENUE % VALIDATION (Sum must be 100)
const revenuePercSum = categories.reduce((sum, cat) => {
  return sum + (parseFloat(cat.percentage) || 0);
}, 0);

// remaining can be +ve or -ve (if exceeded)
const revenueRemaining = 100 - revenuePercSum;

const showRevenueWarning =
  categories.length > 0 &&
  revenuePercSum > 0 && // start showing only after some input
  Math.round(revenuePercSum) !== 100;

  return (
    <div className="body">
      {/* Top layout */}
      <div className="layout">
        {/* LEFT FORM */}
        <div className="main">
          <h2>Room Rate Calculator</h2>

          <form onSubmit={handleSubmit}>
            <label>Property Name</label>
            <input
              className="inp"
              placeholder="Enter property name"
              type="text"
              value={propertyName}
              onChange={(e) => setPropertyName(e.target.value)}
            />

            <label>
              Annual Target Revenue <span style={{ color: "red" }}>*</span>
            </label>
           <input
  className="inp"
  type="number"
  placeholder="₹ Revenue"
  min={0}
  required
  value={annualTarget}
  onChange={(e) => setAnnualTarget(e.target.value)}
/>
            <label>Total Number of Rooms at Property</label>
            <input
              className="inp"
              type="number"
              placeholder="Enter number of rooms"
              min={0}
              value={numRooms}
              onChange={(e) => setNumRooms(e.target.value)}
            />

            <label>
              Number of Room Categories <span style={{ color: "red" }}>*</span>
            </label>
            <input
              className="inp"
              type="number"
              placeholder="Enter number here"
              min={0}
              max={10}
              required
              value={numCategories}
              onChange={handleNumCategoriesChange}
            />

            <label>
              Overall Occupancy % <span style={{ color: "red" }}>*</span>
            </label>
            <input
              className="inp"
              type="number"
              placeholder="Enter occupancy %"
              min={0}
              max={100}
              required
              value={occupancyPercentage}
              onChange={(e) => setOccupancyPercentage(e.target.value)}
            />

            {/* CATEGORY TABLE */}
            <div className="category-section">
              <h5>ROOM CATEGORY DISTRIBUTION</h5>

              <table className="category-table">
              <thead>
  <tr>
    <th>
      Category Name <span style={{ color: "red" }}>*</span>
    </th>
    <th>
      No. of Rooms <span style={{ color: "red" }}>*</span>
    </th>
    <th>
      Revenue % <span style={{ color: "red" }}>*</span>
    </th>
    <th>Average Rate</th>
  </tr>
</thead>
              <tbody>
  {computedCategoryData.rows.map((cat, index) => {
    const perc = parseFloat(cat.percentage) || 0;
    const catRooms = parseFloat(cat.rooms) || 0;

    const revIntoCategory =
      submitted && perc > 0
        ? expectedDailyTarget * (perc / 100)
        : 0;

    const perDayPerRoom =
      submitted && catRooms > 0 && revIntoCategory > 0
        ? revIntoCategory / catRooms
        : 0;

    const midWeekend =
      submitted && perDayPerRoom > 0 ? perDayPerRoom : 0;

    return (
      <tr key={index}>
        <td>
          <input
            required
            type="text"
            className="inp"
            placeholder="Category Name"
            value={cat.name}
            onChange={(e) =>
              updateCategory(index, "name", e.target.value)
            }
          />
        </td>

        <td>
          <input
            required
            type="number"
            className="inp"
            placeholder="Rooms"
            value={cat.rooms}
            onChange={(e) =>
              updateCategory(index, "rooms", e.target.value)
            }
          />
        </td>

        <td>
          <input
            required
            type="number"
            step="0.01"
            className="inp"
            placeholder="%"
            value={cat.percentage}
            onChange={(e) =>
              updateCategory(index, "percentage", e.target.value)
            }
          />
        </td>

        <td>
          {submitted && midWeekend > 0
            ? `₹ ${midWeekend.toFixed(0)}`
            : "-"}
        </td>
      </tr>
    );
  })}
</tbody>
              </table>
              {/* ✅ Warning if Room Count Mismatch */}
{showRoomWarning && (
  <p style={{
    marginTop: "6px",
    color: "red",
    fontSize: "12px",
    fontWeight: "600",
    textAlign: "center"
  }}>
    ⚠ Total Rooms = {totalRoomsNum}, but Category Rooms Sum = {categoryRoomsSum}.
    Please match both values.
  </p>
)}

{/* ✅ Warning if Revenue % Sum is not 100 */}
{showRevenueWarning && (
  <p
    style={{
      marginTop: "6px",
      color: "red",
      fontSize: "12px",
      fontWeight: "600",
      textAlign: "center",
    }}
  >
    ⚠ Revenue % total is {revenuePercSum.toFixed(0)}%.
    {revenueRemaining > 0
      ? ` Add remaining ${revenueRemaining.toFixed(0)}% to complete 100%.`
      : ` Reduce ${(Math.abs(revenueRemaining)).toFixed(0)}% to make it 100%.`}
  </p>
)}


            </div>

{/* CITY PRESET DROPDOWN STARTS HERE */}
<div style={{ marginTop: 10 }}>
  <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
    Select City Preset
  </label>

  <select
    className="inp"
    value={selectedCityPreset}
    onChange={(e) => {
      const city = e.target.value;
      setSelectedCityPreset(city);
      applyCityPreset(city);
    }}
  >
    <option value="">Select City</option>
    {Object.keys(CITY_PRESETS).map((city) => (
      <option key={city} value={city}>
        {city}
      </option>
    ))}
  </select>

  <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
    Auto-applies Low/Mid/Peak months and Occupancy %. You can still edit after selecting.
  </div>
</div>
{/* CITY PRESET DROPDOWN ENDS HERE */}
            
            {/* SEASONS */}
            <div className="season-block">
              <h5>SEASONAL TRENDS</h5>

              <label>Low Seasons *</label>
              <div className="rrc-calc">
              <select
                multiple
                className="low-select"
                value={lowSeasons}
                onChange={(e) => handleMultiSelectChange(e, setLowSeasons)}
              >
                {months.map((m) => (
                  <option
                    key={`low-${m}`}
                    value={m}
                    disabled={isMonthInOtherSeason(m, "low")}
                  >
                    {m}
                  </option>
                ))}
              </select></div>

              <label>Mid Seasons *</label>
              <div className="rrc-calc">
              <select
                multiple
                className="mid-select"
                value={midSeasons}
                onChange={(e) => handleMultiSelectChange(e, setMidSeasons)}
              >
                {months.map((m) => (
                  <option
                    key={`mid-${m}`}
                    value={m}
                    disabled={isMonthInOtherSeason(m, "mid")}
                  >
                    {m}
                  </option>
                ))}
              </select></div>

              <label>High Seasons *</label>
              <div className="rrc-calc">
              <select
                multiple
                className="high-select"
                value={highSeasons}
                onChange={(e) => handleMultiSelectChange(e, setHighSeasons)}
              >
                {months.map((m) => (
                  <option
                    key={`high-${m}`}
                    value={m}
                    disabled={isMonthInOtherSeason(m, "high")}
                  >
                    {m}
                  </option>
                ))}
              </select></div>
            </div>

            {/* SEASON OCCUPANCY % */}
            <div className="season-block" style={{ marginTop: "12px" }}>
              <h5>SEASON OCCUPANCY %</h5>
              <table className="category-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>
                      Select Occupancy % <span style={{ color: "red" }}>*</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => {
                    const options = getOccupancyOptionsForMonth(m);
                    const selected = monthOccupancies[m] || null;
                    const season = getSeasonForMonth(m);

                    return (
                      <tr key={`occ-${m}`}>
                        <td
                          className={`season-month-label ${
                            season ? `season-${season}` : ""
                          }`}
                        >
                          {m}
                        </td>
                        <td>
                          {season && options.length > 0 ? (
                            <div className="season-occ-options">
                              {options.map((val) => (
                                <label
                                  key={`${m}-${val}`}
                                  className="season-occ-option"
                                >
                                  <input
                                    type="radio"
                                    name={`occ-${m}`}
                                    value={val}
                                    required
                                    checked={selected === val}
                                    onChange={() =>
                                      handleMonthOccupancyChange(m, val)
                                    }
                                  />
                                  {val}%
                                </label>
                              ))}
                            </div>
                          ) : (
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              Select season above
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="submitbtn">
              <button type="submit">GET TARGETS</button>
            </div>
          </form>
        </div>

        {/* RIGHT PANEL */}
        <div className="right-panel">
          {/* Seasonal Rates table */}
          <div className="summary-container">
            <h3>Seasonal Rates</h3>
            <table className="summary-table seasonal-table">
              <thead>
                <tr>
                  <th rowSpan="3">Room Category</th>
                  <th rowSpan="3">No. of Rooms</th>
                  <th colSpan="2" className="season-header low-header">
                    Low Seasons
                  </th>
                  <th colSpan="2" className="season-header mid-header">
                    Mid Seasons
                  </th>
                  <th colSpan="2" className="season-header peak-header">
                    Peak Seasons
                  </th>
                </tr>
                <tr className="season-months-row">
                  <th colSpan="2" className="season-months-cell low-months">
                    {joinedLow}
                  </th>
                  <th colSpan="2" className="season-months-cell mid-months">
                    {joinedMid}
                  </th>
                  <th colSpan="2" className="season-months-cell peak-months">
                    {joinedHigh}
                  </th>
                </tr>
                <tr className="season-weektype-row">
                  <th>Weekdays</th>
                  <th>Weekends</th>
                  <th>Weekdays</th>
                  <th>Weekends</th>
                  <th>Weekdays</th>
                  <th>Weekends</th>
                </tr>
              </thead>
              <tbody>
{computedCategoryData.rows.map((cat, index) => {        
  const catPerc = parseFloat(cat.percentage) || 0;
                  const catRooms = parseFloat(cat.rooms) || 0;

                  // Rev into categories = expected daily target * revenue%
                  const revIntoCategory =
                    submitted && catPerc > 0
                      ? expectedDailyTarget * (catPerc / 100)
                      : 0;

                  // Per day per room rev = revIntoCategory / rooms
                  const perDayPerRoom =
                    submitted && catRooms > 0
                      ? revIntoCategory / catRooms
                      : 0;

                  // Using perDayPerRoom (P) to fill season cells
                const midWeekend = submitted && perDayPerRoom > 0 ? perDayPerRoom : 0;

const lowWeekdayPercent = getPercentNumber(seasonRatePercents.lowWeekday, 30);
const lowWeekendPercent = getPercentNumber(seasonRatePercents.lowWeekend, 20);
const midWeekdayPercent = getPercentNumber(seasonRatePercents.midWeekday, 10);
const peakWeekdayPercent = getPercentNumber(seasonRatePercents.peakWeekday, 20);
const peakWeekendPercent = getPercentNumber(seasonRatePercents.peakWeekend, 30);

const lowWeekday =
  submitted && midWeekend > 0
    ? midWeekend * (1 - lowWeekdayPercent / 100)
    : 0;

const lowWeekend =
  submitted && midWeekend > 0
    ? midWeekend * (1 - lowWeekendPercent / 100)
    : 0;

const midWeekday =
  submitted && midWeekend > 0
    ? midWeekend * (1 - midWeekdayPercent / 100)
    : 0;

const peakWeekday =
  submitted && midWeekend > 0
    ? midWeekend * (1 + peakWeekdayPercent / 100)
    : 0;

const peakWeekend =
  submitted && midWeekend > 0
    ? midWeekend * (1 + peakWeekendPercent / 100)
    : 0;

    

                  return (
                    <tr key={`season-${index}`}>
                      <td>{submitted && cat.name ? cat.name : "-"}</td>
                      <td>{submitted && catRooms ? catRooms : "-"}</td>

                      {/* LOW SEASON */}
{/* LOW SEASON */}
<td>
  <div className="season-rate-cell">
    <span>
      {submitted && lowWeekday > 0 ? `₹ ${lowWeekday.toFixed(0)}` : "-"}
    </span>

    <input
      className="season-percent-input"
      type="number"
      step="1"
      min="0"
      max="9999"
      value={seasonRatePercents.lowWeekday}
      onChange={(e) =>
        updateSeasonRatePercent("lowWeekday", e.target.value)
      }
    />
  </div>
</td>

<td>
  <div className="season-rate-cell">
    <span>
      {submitted && lowWeekend > 0 ? `₹ ${lowWeekend.toFixed(0)}` : "-"}
    </span>

    <input
      className="season-percent-input"
      type="number"
      step="1"
      min="0"
      max="9999"
      value={seasonRatePercents.lowWeekend}
      onChange={(e) =>
        updateSeasonRatePercent("lowWeekend", e.target.value)
      }
    />
  </div>
</td>

{/* MID SEASON */}
<td>
  <div className="season-rate-cell">
    <span>
      {submitted && midWeekday > 0 ? `₹ ${midWeekday.toFixed(0)}` : "-"}
    </span>

    <input
      className="season-percent-input"
      type="number"
      step="1"
      min="0"
      max="9999"
      value={seasonRatePercents.midWeekday}
      onChange={(e) =>
        updateSeasonRatePercent("midWeekday", e.target.value)
      }
    />
  </div>
</td>

<td>
  <div className="season-rate-cell">
    <span>
      {submitted && midWeekend > 0 ? `₹ ${midWeekend.toFixed(0)}` : "-"}
    </span>
  </div>
</td>

{/* PEAK SEASON */}
<td>
  <div className="season-rate-cell">
    <span>
      {submitted && peakWeekday > 0 ? `₹ ${peakWeekday.toFixed(0)}` : "-"}
    </span>

    <input
      className="season-percent-input"
      type="number"
      step="1"
      min="0"
      max="9999"
      value={seasonRatePercents.peakWeekday}
      onChange={(e) =>
        updateSeasonRatePercent("peakWeekday", e.target.value)
      }
    />
  </div>
</td>

<td>
  <div className="season-rate-cell">
    <span>
      {submitted && peakWeekend > 0 ? `₹ ${peakWeekend.toFixed(0)}` : "-"}
    </span>

    <input
      className="season-percent-input"
      type="number"
      step="1"
      min="0"
      max="9999"
      value={seasonRatePercents.peakWeekend}
      onChange={(e) =>
        updateSeasonRatePercent("peakWeekend", e.target.value)
      }
    />
  </div>
</td></tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Annual Desired Revenue table */}
          <div className="summary-container">
            <h3>Annual Desired Revenue</h3>
            <table className="summary-table annual-table">
              <thead>
                <tr>
                  <th>Months</th>
                  <th>Occupancy</th>
                  <th>No. of Days</th>
                  <th>Revenue</th>
                  <th>ADR</th>
                </tr>
              </thead>
              <tbody>
                {annualRows.map((row) => (
                  <tr key={row.month}>
                    <td>{row.month}</td>
                    <td>
                      {row.occSelected > 0 ? `${row.occSelected}%` : "-"}
                    </td>
                    <td>{row.daysInMonth}</td>
<td>
  {submitted && row.targetRevenue > 0
    ? `₹ ${row.targetRevenue.toFixed(0)}`
    : "-"}
</td>

                    <td>
                      {submitted && row.adr > 0
                        ? `₹ ${row.adr.toFixed(0)}` 
                        : "-"}
                    </td>
                  </tr>
                ))}

                <tr className="spacer-row">
                  <td colSpan="5"></td>
                </tr>

                {/* FINAL TOTAL ROW */}
                <tr className="total-row">
                  <td>Total</td>
                  <td>
                    {submitted && avgOcc > 0
                      ? `${avgOcc.toFixed(0)}%`
                      : "-"}
                  </td>
                  <td></td>
                  <td>
                    {submitted && totalRevenue > 0
                      ? `₹ ${totalRevenue.toFixed(0)}`
                      : "-"}
                  </td>
                  <td>
                    {submitted && avgAdr > 0
                      ? `₹ ${avgAdr.toFixed(0)}`
                      : "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ASSIGN TO TABLE */}
          <div className="summary-container">
            <h3>Assign To</h3>
            <form onSubmit={handleAssignSubmit}>
              <table className="summary-table assign-table">
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th>Select</th>
                  </tr>
                </thead>
                <tbody>
                  {assignOptions.map((opt) => (
                    <tr key={opt}>
                      <td>{opt}</td>
                      <td>
                        <input
                          type="radio"
                          name="assignTo"
                          value={opt}
                          checked={assignedTo === opt}
                          onChange={(e) => setAssignedTo(e.target.value)}
                          required
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="submitbtn" style={{ marginTop: "8px" }}>
                <button type="submit">SUBMIT</button>
              </div>
            </form>
          </div>

          
        

          
          {/*CALENDAR UI STARTS HERE*/}
          {/* LIVE CALENDAR */}
<div className="summary-container">
  <h3>Calendar</h3>

  <div className="live-cal-header">
    <button type="button" className="cal-nav" onClick={goPrevMonth}>
      ←
    </button>

    <div className="cal-title">
      {monthNames[calMonth]} {calYear}
    </div>

    <button type="button" className="cal-nav" onClick={goNextMonth}>
      →
    </button>
  </div>

{/* WEEKLY VALIDATION UI 3 MARCH STARTS HERE */}
  <div style={{ marginTop: 8, fontSize: 12 }}>
  {weekDiffs.map(({ wi, req, sum, diff }) => (
    <div key={wi} style={{ marginBottom: 4 }}>
      <b>Week {wi + 1}</b>: Sum = {sum.toFixed(2)} / Required = {req}
      <span style={{ marginLeft: 8, color: diff === 0 ? "green" : "red", fontWeight: 700 }}>
        {diff === 0 ? "✅ OK" : `⚠ Remaining: ${diff > 0 ? diff : `-${Math.abs(diff)}`}`}
      </span>
    </div>
  ))}
</div>
  {/* WEEKLY VALIDATION ENDS HERE */}
  
{/* CALENDAR WK VARIABLES START HERE */}
{/* ✅ WEEK TEST TABLE (UI only) */}
<table className="summary-table" style={{ width: "100%", marginTop: 10 }}>
  <thead>
    <tr>
      <th style={{ textAlign: "center" }}>Week 1</th>
      <th style={{ textAlign: "center" }}>Week 2</th>
      <th style={{ textAlign: "center" }}>Week 3</th>
      <th style={{ textAlign: "center" }}>Week 4</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>
        <input
          className="inp"
          type="number"
          step="0.01"     // ✅ allow decimals like 1.2
          min="0"
          placeholder="Enter"
          value={currentWeekInputs.week1}
          onChange={(e) => updateWeekInput(calYear, calMonth, "week1", e.target.value)}
        />
      </td>
      <td>
        <input
          className="inp"
          type="number"
          step="0.01"     // ✅ allow decimals like 1.2
          min="0"
          placeholder="Enter"
          value={currentWeekInputs.week2}
          onChange={(e) => updateWeekInput(calYear, calMonth, "week2", e.target.value)}
        />
      </td>
      <td>
        <input
          className="inp"
          type="number"
          step="0.01"     // ✅ allow decimals like 1.2
          min="0"
          placeholder="Enter"
          value={currentWeekInputs.week3}
          onChange={(e) => updateWeekInput(calYear, calMonth, "week3", e.target.value)}
        />
      </td>
      <td>
        <input
          className="inp"
          type="number"
          step="0.01"     // ✅ allow decimals like 1.2
          min="0"
          placeholder="Enter"
          value={currentWeekInputs.week4}
          onChange={(e) => updateWeekInput(calYear, calMonth, "week4", e.target.value)}
        />
      </td>
    </tr>
  </tbody>
</table>
  {/* CALENDAR WEEK VARIABLES ENDS HERE */}
  
  <div className="cal-weekdays">
    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
      <div key={d} className="cal-weekday">{d}</div>
    ))}
  </div>

  <div className="cal-grid">
    {(() => {
      const blanks = getFirstDayOfMonth(calYear, calMonth);
      const days = getDaysInMonth(calYear, calMonth);

      const cells = [];

      // leading blanks
      for (let i = 0; i < blanks; i++) {
        cells.push(<div key={`b-${i}`} className="cal-cell cal-blank" />);
      }

      // day cells
  for (let day = 1; day <= days; day++) {
  const dateObj = new Date(calYear, calMonth, day);
  const isToday = isSameDay(dateObj, today);
  const isSelected = selectedDate && isSameDay(dateObj, selectedDate);

  const iso = toISODate(dateObj);
  const baseTarget = dailyTargets[iso] || 0;

  const inputStr = dayInputs[iso];              // string or undefined
  const hasInput = inputStr !== undefined && inputStr !== "";  
  const mult = hasInput ? parseFloat(inputStr) : 1;

  const finalTarget =
    submitted && monthTarget > 0
      ? hasInput
        ? Math.round(baseTarget * (Number.isFinite(mult) ? mult : 1))
        : Math.round(baseTarget)
      : 0;

  const dayOfWeek = dateObj.getDay();
  const isPeakTravelDay = dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0;

 cells.push(
  <div
    key={`d-${day}`}
    role="button"
    tabIndex={0}
    className={`cal-cell cal-day ${isPeakTravelDay ? "cal-peak" : ""} ${
      isToday ? "cal-today" : ""
    } ${isSelected ? "cal-selected" : ""}`}
    onClick={() => setSelectedDate(dateObj)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") setSelectedDate(dateObj);
    }}
    title={dateObj.toDateString()}
  >
    <div className="cal-daynum">{day}</div>

    <div className="cal-targetrow">
      <div className="cal-daytarget">
        {submitted && monthTarget > 0
          ? `₹ ${Number(finalTarget).toLocaleString("en-IN")}`
          : ""}
      </div>

      <input
        className="cal-dayinput"
        type="number"
        step="0.01"
        min="0"
        value={inputStr ?? ""}
        onMouseDown={(e) => e.stopPropagation()} // ✅ prevents cell click while focusing input
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => updateDayInput(iso, e.target.value)}
      />
    </div>
  </div>
);
}

      return cells;
    })()}
  </div>

  <div className="cal-footer">
    <span>
      Selected:{" "}
      {selectedDate ? selectedDate.toDateString() : "-"}
    </span>
  </div>
</div>
          {/*CALENDAR UI ENDS HERE*/}
          
        </div>
      </div>
      <div className="copyright_footer">© 2026 RevX Hospitality. All Rights Reserved. Developed at <span style={{ color: 'green' }}>RevX Hospitality</span>.</div>
    </div>
  );
}

export default RevenueForecastCalc;
